import moment, {Moment} from 'moment';

/**
 * "Now" as the given IANA zone's wall clock: a plain Moment whose *fields* hold the zone's
 * current wall time. The app is wall-clock throughout — API dates are offset-less strings the
 * backend reads as company-local — so deriving "now"/"today" is the one zone-aware operation;
 * every downstream `startOf`/`endOf`/format/compare works on the fields and needs no zone.
 *
 * `Intl` carries the tz database natively, which is why this exists instead of pulling in
 * `moment-timezone`. A falsy or invalid zone (Intl throws a RangeError) falls back to the
 * browser clock, so consumers can pass a possibly-missing company zone straight through.
 *
 * @param at The instant to convert — for tests; defaults to the actual now.
 */
export function nowInTimezone(timezone: string | null | undefined, at: Date = new Date()): Moment {
  if (!timezone) {
    return moment(at);
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value;
    return moment(`${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}`);
  } catch {
    return moment(at);
  }
}
